// Run has its own list of instantly recognisable words (built by
// scripts/build-run-words.mjs). The Solo bots' database is only consulted as
// a second opinion on whether a decoy could fairly count for the round.
import runWords from './runWords.json';
import wordDB from '../../wordDatabase.json';
import { EXCLUDED, ALSO_COUNTS_AS } from './wordRules';

export const RUN_CATEGORIES = ['names', 'animals', 'places', 'things', 'food', 'jobs'];

export const CATEGORY_LABEL = {
  names: 'Names',
  animals: 'Animals',
  places: 'Places',
  things: 'Things',
  food: 'Food',
  jobs: 'Jobs',
};

const CATEGORY_NOUN = {
  names: 'a name',
  animals: 'an animal',
  places: 'a place',
  things: 'a thing',
  food: 'a food',
  jobs: 'a job',
};

export const categoryNoun = (category) => CATEGORY_NOUN[category];

const norm = (w) => w.trim().toLowerCase();
const display = (w) => w.charAt(0).toUpperCase() + w.slice(1);

function poolFor(letter, category) {
  return (runWords[letter]?.[category] || []).filter(
    (w) => w.length <= 12 && w[0].toUpperCase() === letter && !EXCLUDED.has(norm(w))
  );
}

const answerPool = (letter, category) => poolFor(letter, category).filter((w) => !ALSO_COUNTS_AS[norm(w)]);

// A letter + category pair is only dealt when there are enough clear answers.
export const MIN_ANSWERS = 8;
export const PLAYABLE = Object.fromEntries(
  RUN_CATEGORIES.map((c) => [c, Object.keys(runWords).filter((l) => answerPool(l, c).length >= MIN_ANSWERS)])
);

const LETTERS = Object.keys(runWords).filter((l) => RUN_CATEGORIES.some((c) => poolFor(l, c).length));
const ALL_WORDS = LETTERS.flatMap((letter) =>
  RUN_CATEGORIES.flatMap((category) => poolFor(letter, category).map((word) => ({ word, letter, category })))
);

export function isInCategory(word, letter, category) {
  const n = norm(word);
  if (ALSO_COUNTS_AS[n]?.includes(category)) return true;
  if ((runWords[letter]?.[category] || []).some((w) => norm(w) === n)) return true;
  const b = wordDB[letter]?.[category];
  return !!b && [...(b.easy || []), ...(b.medium || []), ...(b.hard || [])].some((w) => norm(w) === n);
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeRng(seed) {
  const next = mulberry32(seed);
  const pick = (arr) => arr[Math.floor(next() * arr.length)];
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(next() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };
  return { next, pick, shuffle };
}

export function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// --- Difficulty ---------------------------------------------------------

const TIERS = ['easy', 'medium', 'hard', 'near'];
const WARMUP_GATES = 2;

export function difficultyFor(stageIndex, gateIndex, stageLength) {
  const base = Math.min(0.75, stageIndex * 0.1);
  if (gateIndex < WARMUP_GATES) return Math.min(base, 0.45);
  const ramp = 0.25 * (gateIndex / Math.max(1, stageLength - 1));
  return Math.min(1, base + ramp);
}

export function tierFor(d) {
  if (d < 0.22) return 'easy';
  if (d < 0.45) return 'medium';
  if (d < 0.7) return 'hard';
  return 'near';
}

export const revealLeadFor = (d, { mode = 'timed', stageIndex = 0 } = {}) =>
  mode === 'endless' && stageIndex >= 3 ? Math.max(2, 3.6 - 1.5 * d) : Infinity;
// Early gates are far apart so there's time to read; the gap tightens with difficulty.
// Timed is the rapid-fire mode: words come closer together, with fewer obstacles between.
const gateGapFor = (d, mode) => (mode === 'endless' ? 44 - 20 * d : 34 - 12 * d);
const TIMED_OBSTACLE_RATE = 0.5;

// The pace starts brisk, clicks up with every new category and builds with gates played,
// plus up to 20% while on a streak, so a good player is flying early and a struggling one
// gets breathing room back.
export const SPEED_START = 1.15;
export function speedFor(gatesResolved, streak = 0, stagesCleared = 0) {
  return SPEED_START + Math.min(0.45, gatesResolved * 0.004 + stagesCleared * 0.03) + Math.min(0.2, streak * 0.025);
}

export const TURBO_SPEED = 1.7;
export const TURBO_POINTS = 1.2;

export const CLASSES = ['Primary 1', 'Primary 2', 'Primary 3', 'Primary 4', 'Primary 5', 'Primary 6', 'JSS 1', 'JSS 2', 'JSS 3', 'SS 1', 'SS 2', 'SS 3', 'University'];
export const STAGES_PER_CLASS = 2;

// Endless promotes from Primary 1 every two stages, so University takes a long run.
// Timed sits in one class picked from the seed; University stays a reward.
export function classFor(mode, seed, stageIndex) {
  if (mode === 'endless') return Math.min(CLASSES.length - 1, Math.floor(Math.max(0, stageIndex) / STAGES_PER_CLASS));
  return hashString(`class:${seed}`) % (CLASSES.length - 1);
}

// --- Scoring ------------------------------------------------------------

const MULTIPLIER_AT = [
  [10, 4],
  [6, 3],
  [3, 2],
];

export function multiplierFor(streak) {
  for (const [at, mult] of MULTIPLIER_AT) if (streak >= at) return mult;
  return 1;
}

export function dropTier(streak) {
  if (streak >= 10) return 6;
  if (streak >= 6) return 3;
  return 0;
}

const WRONG_PENALTY_SEC = 3;
const OBSTACLE_PENALTY = 50;
const CLUTCH_BONUS = 50;
const RUSH_EVERY = 10;
const RUSH_GATES = 5;

// --- Gold stars & the Hall Pass -------------------------------------------

const STAR_POINTS = 10;
// Spare erasers (extra lives) are Endless-only and rare: one may float high over a stage's
// run-up, past stage 3, and a full star meter sometimes adds one while a life is missing.
export const SPARE_CHANCE = 0.12;
export const SPARE_FROM_STAGE = 3;
export const SPARE_ON_PASS = 0.25;
export const SPARE_BONUS = 500;
export const SPARE_Y = 2.1;
// Timed-only: a clock floats high between some gates, and a streak earns a little time too.
// Bonus time is capped, so Timed stays the quick mode.
export const CLOCK_CHANCE = 0.14;
export const CLOCK_SEC = 5;
export const STREAK_TIME_EVERY = 5;
export const STREAK_TIME_SEC = 2;
export const TIME_BONUS_CAP = 75;
// A rare second chance at the moment a run would end, never in its first minute and
// at most once: "Up NEPA!" hands back one eraser in Endless, "Mummy has come!" adds
// a few seconds in Timed. The next word after it can't hurt, so the save isn't wasted.
export const RESCUE_CHANCE = 1 / 8;
export const RESCUE_MIN_SEC = 60;
export const RESCUE_TIME_SEC = 10;
// Letters of the weekly word float over some stage run-ups, in order.
export const LETTER_CHANCE = 0.35;
export const STAR_METER = 40;
// A full meter makes the Hall Pass ready; the player starts it. While it runs the pencil
// is faster and unstoppable: obstacles are smashed and any word picked scores.
export const HALL_PASS_SEC = 6;
export const DASH_SPEED = 1.6;
const STAR_GAP = 2.6;
// Resting height of a star's centre, and the peak a jump reaches (JUMP_V² / 2g in the scene).
export const STAR_Y = 0.8;
const JUMP_PEAK = 1.6;
const JUMP_ARC = 6.4;

// --- Obstacles ----------------------------------------------------------

// Sizes in world units. `depth` runs along the track, `height` is what a jump must clear.
export const OBSTACLES = {
  eraser: { depth: 0.9, halfWidth: 1.0, height: 0.75 },
  ruler: { depth: 0.55, halfWidth: 5.2, height: 0.32 },
  ink: { depth: 8, halfWidth: 1.2, height: 0.12 },
  roller: { depth: 0.64, halfWidth: 1.3, height: 0.64, speed: 3.4 },
};

const others = (lane) => [0, 1, 2].filter((l) => l !== lane);

const PATTERNS = [
  { name: 'eraser', minD: 0, weight: 3, build: ({ rng }) => [{ type: 'eraser', lanes: [rng.pick([0, 1, 2])], at: 0.5 }] },
  { name: 'ruler', minD: 0.08, weight: 3, build: () => [{ type: 'ruler', lanes: [0, 1, 2], at: 0.5 }] },
  { name: 'ink', minD: 0.2, weight: 2, build: ({ rng }) => [{ type: 'ink', lanes: [rng.pick([0, 1, 2])], at: 0.15 }] },
  {
    name: 'double-eraser',
    minD: 0.25,
    weight: 2,
    build: ({ rng }) => [{ type: 'eraser', lanes: others(rng.pick([0, 1, 2])), at: 0.5 }],
  },
  {
    name: 'roller',
    minD: 0.35,
    weight: 2,
    build: ({ rng }) => [{ type: 'roller', lanes: [0, 1, 2], at: 0.5, dir: rng.next() < 0.5 ? -1 : 1 }],
  },
  {
    name: 'ruler-eraser',
    minD: 0.5,
    weight: 2,
    build: ({ rng }) => [
      { type: 'ruler', lanes: [0, 1, 2], at: 0.2 },
      { type: 'eraser', lanes: [rng.pick([0, 1, 2])], at: 0.85 },
    ],
  },
  {
    name: 'double-ink',
    minD: 0.5,
    weight: 1,
    build: ({ rng }) => [{ type: 'ink', lanes: others(rng.pick([0, 1, 2])), at: 0.1 }],
  },
  { name: 'jump-choose', minD: 0.8, weight: 2, build: () => [{ type: 'ruler', lanes: [0, 1, 2], at: 1 }] },
  {
    name: 'ink-on-answer',
    minD: 0.85,
    weight: 1,
    build: ({ answerLane }) => [{ type: 'ink', lanes: [answerLane], at: 0 }],
  },
];

function pickPattern(rng, d) {
  const eligible = PATTERNS.filter((p) => d >= p.minD);
  const total = eligible.reduce((n, p) => n + p.weight, 0);
  let roll = rng.next() * total;
  for (const p of eligible) {
    roll -= p.weight;
    if (roll <= 0) return p;
  }
  return eligible[eligible.length - 1];
}

// Gap between a gate and the next is split: clear air after the previous
// gate, the obstacle window, then enough run-up to read and pick the next card.
const AFTER_GATE = 9;
const BEFORE_GATE = 10;

// --- Run ----------------------------------------------------------------

// Each stage starts on a fresh notebook page, so stage markers sit on page boundaries.
export const PAGE_LEN = 21.6;
const STAGE_LENGTH = { min: 6, max: 9 };
const STAGE_LEAD = 40;
const STAGE_TAIL = 8;
const FIRST_MARKER_AT = PAGE_LEN;

export function createRun({
  seed = Date.now(),
  mode = 'timed',
  durationSec = 75,
  lives = 3,
  tutorial = false,
  letters = [],
  rescueChance = RESCUE_CHANCE,
  rescueMinSec = RESCUE_MIN_SEC,
} = {}) {
  const rng = makeRng(seed);
  // Stars draw from their own stream so they never reshuffle the words or obstacles.
  const starRng = makeRng((seed ^ 0x5bd1e995) >>> 0);
  // Separate streams again: spares never shift stars or words, and play never shifts the course.
  const spareRng = makeRng((seed ^ 0x2545f491) >>> 0);
  const bonusRng = makeRng((seed ^ 0x68e31da4) >>> 0);
  const clockRng = makeRng((seed ^ 0x1b873593) >>> 0);
  const letterRng = makeRng((seed ^ 0x7f4a7c15) >>> 0);
  const rescueRng = makeRng((seed ^ 0x3c6ef372) >>> 0);
  const maxLives = lives;
  const timed = mode !== 'endless';

  const state = {
    mode,
    score: 0,
    streak: 0,
    bestStreak: 0,
    multiplier: 1,
    rush: 0,
    correct: 0,
    total: 0,
    lives,
    timeLeft: durationSec,
    duration: durationSec,
    elapsed: 0,
    over: false,
    practice: false,
    obstaclesHit: 0,
    obstaclesCleared: 0,
    skipped: 0,
    stagesCleared: 0,
    stage: null,
    misses: [],
    turbo: false,
    classIndex: classFor(mode, seed, 0),
    stars: 0,
    meter: 0,
    pass: 0,
    passes: 0,
    spares: 0,
    passReady: false,
    dashGates: 0,
    distance: 0,
    obstaclesSmashed: 0,
    clocks: 0,
    timeAdded: 0,
    letters: [],
    rescued: null,
    grace: 0,
  };

  let itemSeq = 0;
  let cursor = 0;
  let stage = null;
  let stageIndex = -1;
  let gateInStage = 0;
  let lastLetter = null;
  let lastCategory = null;
  let stageCorrect = 0;
  const usedInStage = new Set();
  const queue = [];

  function newStage() {
    stageIndex += 1;
    let letter;
    let category;
    const categories = RUN_CATEGORIES.filter((c) => c !== lastCategory && PLAYABLE[c].length);
    category = tutorial && stageIndex === 0 ? 'animals' : rng.pick(categories);
    const letters = PLAYABLE[category].filter((l) => l !== lastLetter);
    letter = rng.pick(letters.length ? letters : PLAYABLE[category]);
    lastLetter = letter;
    lastCategory = category;
    const tutorialStage = tutorial && stageIndex === 0;
    stage = {
      index: stageIndex,
      letter,
      category,
      length: tutorialStage
        ? 5
        : STAGE_LENGTH.min + Math.floor(rng.next() * (STAGE_LENGTH.max - STAGE_LENGTH.min + 1)),
      tutorial: tutorialStage,
    };
    gateInStage = 0;
    usedInStage.clear();
  }

  function buildRow(d) {
    const { letter, category } = stage;
    const tier = stage.tutorial ? 'easy' : tierFor(d);
    const pool = answerPool(letter, category);

    let answer = rng.pick(pool);
    for (let tries = 0; tries < 12 && usedInStage.has(norm(answer)); tries++) answer = rng.pick(pool);
    usedInStage.add(norm(answer));

    const taken = new Set([norm(answer)]);
    // Some rows leave one lane open: running through it skips the word.
    const freeLane = !stage.tutorial && gateInStage > 0 && rng.next() < 0.3 - 0.15 * d;
    const decoys = buildDecoys({ tier, letter, category, answer, rng, taken }).slice(0, freeLane ? 1 : 2);
    const options = rng.shuffle([
      { word: display(answer), valid: true },
      ...decoys.map((w) => ({ word: display(w), valid: false })),
    ]);
    if (freeLane) options.splice(Math.floor(rng.next() * 3), 0, null);
    return {
      stage: { ...stage },
      stageStart: gateInStage === 0,
      lastInStage: gateInStage === stage.length - 1,
      gateInStage,
      tier,
      options,
      answerLane: options.findIndex((o) => o?.valid),
    };
  }

  function push(item) {
    const full = { id: itemSeq++, ...item };
    queue.push(full);
    return full;
  }

  function placeObstacles(from, to, d, answerLane) {
    const start = from + AFTER_GATE;
    const end = to - BEFORE_GATE;
    if (end - start < 3) return [];
    const pattern = pickPattern(rng, d);
    return pattern.build({ rng, answerLane }).map((o) => {
      const spec = OBSTACLES[o.type];
      let s = start + (end - start - spec.depth) * o.at;
      if (o.type === 'ink') s = Math.min(s, end - spec.depth - 0.5);
      return push({ kind: 'obstacle', type: o.type, lanes: o.lanes, dir: o.dir || 0, s, pattern: pattern.name });
    });
  }

  function starTrail(s0, lanes) {
    lanes.forEach((lane, i) => lane !== null && push({ kind: 'star', s: s0 + i * STAR_GAP, lane, h: STAR_Y }));
  }

  // Stars float along a jump's path, so only a jump over the obstacle collects them.
  function starArc(center, lane) {
    for (const f of [0.22, 0.36, 0.5, 0.64, 0.78]) {
      push({ kind: 'star', s: center + (f - 0.5) * JUMP_ARC, lane, h: STAR_Y + 4 * JUMP_PEAK * f * (1 - f) });
    }
  }

  // Stars sit between gates. Sometimes a trail runs straight into a wrong card:
  // the player chooses between the stars and the word.
  function placeStars(from, to, obstacles, row) {
    if (starRng.next() > 0.65) return;
    const jumpable = obstacles.filter((o) => o.type !== 'ink');
    for (const o of jumpable) {
      const lane = o.type === 'eraser' ? starRng.pick(o.lanes) : starRng.pick([0, 1, 2]);
      starArc(o.s + OBSTACLES[o.type].depth / 2, lane);
    }
    if (jumpable.length) return;

    const decoys = row.options.flatMap((o, lane) => (o && !o.valid ? [lane] : []));
    if (decoys.length && starRng.next() < 0.3) {
      const lane = starRng.pick(decoys);
      const clear = Math.max(
        to - 2 - STAR_GAP * 5,
        ...obstacles.filter((o) => o.lanes.includes(lane)).map((o) => o.s + OBSTACLES[o.type].depth + 1.2)
      );
      const count = Math.floor((to - 2 - clear) / STAR_GAP) + 1;
      if (count >= 3) starTrail(to - 2 - (count - 1) * STAR_GAP, Array(count).fill(lane));
      return;
    }
    if (obstacles.length) return;

    const count = 5 + Math.floor(starRng.next() * 4);
    const start = from + AFTER_GATE;
    const room = to - BEFORE_GATE - start - count * STAR_GAP;
    if (room < 0) return;
    const lane = starRng.pick([0, 1, 2]);
    let lanes = Array(count).fill(lane);
    if (starRng.next() < 0.4) {
      const next = lane === 1 ? starRng.pick([0, 2]) : 1;
      const half = Math.floor(count / 2);
      lanes = lanes.map((l, i) => (i < half ? l : i === half ? null : next));
    }
    starTrail(start + starRng.next() * room, lanes);
  }

  const TUTORIAL_SCRIPT = [
    { hint: 'lanes' },
    { hint: null },
    { hint: 'jump', obstacle: { type: 'ruler', lanes: [0, 1, 2] } },
    { hint: 'dodge', obstacle: { type: 'eraser', lanes: [1] } },
    { hint: null, obstacle: { type: 'ruler', lanes: [0, 1, 2] } },
  ];

  function extend() {
    if (!stage || gateInStage >= stage.length) {
      const markerAt = stage ? Math.ceil((cursor + STAGE_TAIL) / PAGE_LEN) * PAGE_LEN : FIRST_MARKER_AT;
      newStage();
      push({
        kind: 'stage',
        s: markerAt,
        stage: { ...stage },
        hint: tutorial && stageIndex === 1 ? 'go' : null,
      });
      cursor = markerAt + STAGE_LEAD;
      const row = buildRow(difficultyFor(stageIndex, 0, stage.length));
      if (!stage.tutorial) placeStars(markerAt, cursor, [], row);
      if (mode === 'endless' && !tutorial && stageIndex >= SPARE_FROM_STAGE && spareRng.next() < SPARE_CHANCE) {
        push({ kind: 'spare', s: markerAt + STAGE_LEAD * 0.55, lane: spareRng.pick([0, 1, 2]), h: SPARE_Y });
      }
      if (!tutorial && stageIndex > 0 && state.letters.length < letters.length && letterRng.next() < LETTER_CHANCE) {
        push({ kind: 'letter', s: markerAt + STAGE_LEAD * 0.3, lane: letterRng.pick([0, 1, 2]), h: SPARE_Y });
      }
      push({
        kind: 'gate',
        s: cursor,
        row,
        revealLead: stage.tutorial ? 3.2 : revealLeadFor(0, { mode, stageIndex }),
        hint: stage.tutorial ? 'lanes' : null,
      });
      gateInStage += 1;
      return;
    }

    const d = stage.tutorial ? 0 : difficultyFor(stageIndex, gateInStage, stage.length);
    const from = cursor;
    cursor += stage.tutorial ? 34 : gateGapFor(d, mode);
    const row = buildRow(d);

    if (stage.tutorial) {
      const step = TUTORIAL_SCRIPT[gateInStage] || {};
      if (step.obstacle) {
        push({
          kind: 'obstacle',
          ...step.obstacle,
          dir: 0,
          s: from + (cursor - from) / 2,
          pattern: 'tutorial',
          hint: step.hint,
        });
      }
    } else {
      const obstacles =
        stageIndex > 0 && gateInStage >= WARMUP_GATES && rng.next() < (0.2 + 0.5 * d) * (timed ? TIMED_OBSTACLE_RATE : 1)
          ? placeObstacles(from, cursor, d, row.answerLane)
          : [];
      placeStars(from, cursor, obstacles, row);
      if (timed && !obstacles.length && clockRng.next() < CLOCK_CHANCE) {
        push({ kind: 'clock', s: (from + cursor) / 2, lane: clockRng.pick([0, 1, 2]), h: SPARE_Y });
      }
    }

    push({ kind: 'gate', s: cursor, row, revealLead: stage.tutorial ? 3.2 : revealLeadFor(d, { mode, stageIndex }) });
    gateInStage += 1;
  }

  function nextItem() {
    while (queue.length === 0) {
      extend();
      queue.sort((a, b) => a.s - b.s);
    }
    return queue.shift();
  }

  function enterStage(item) {
    if (state.stage) state.stagesCleared += 1;
    state.stage = item.stage;
    state.practice = !!item.stage.tutorial;
    stageCorrect = 0;
    const next = classFor(state.mode, seed, item.stage.index);
    const promoted = next > state.classIndex;
    state.classIndex = next;
    return { promoted };
  }

  function resolveGate(item, lane, { clutch = false } = {}) {
    if (state.over) return null;
    const row = item.row;
    const dash = state.pass > 0;
    const chosen = row.options[lane];
    const correct = dash ? chosen !== null : !!chosen?.valid;
    if (dash && chosen !== null) state.dashGates += 1;
    const answer = row.options[row.answerLane];
    let points = 0;
    let rushStarted = false;
    let timeAdded = 0;
    let rescue = null;
    const spared = state.grace > 0;
    if (chosen !== null) state.grace = 0;

    if (chosen === null) {
      // A skip: no points and no penalty, but the streak (and its multiplier) starts over,
      // unless the Hall Pass is running.
      if (!dash) {
        state.skipped += 1;
        state.streak = 0;
        state.multiplier = 1;
        state.rush = 0;
      }
      return {
        correct: false,
        skipped: true,
        chosen: null,
        answer,
        points: 0,
        stage: row.stage,
        tier: row.tier,
        stageClear: row.lastInStage ? { correct: stageCorrect, total: row.stage.length } : null,
        snapshot: { ...state },
      };
    }

    state.total += 1;
    if (correct) {
      state.correct += 1;
      stageCorrect += 1;
      state.streak += 1;
      state.bestStreak = Math.max(state.bestStreak, state.streak);
      state.multiplier = multiplierFor(state.streak);
      points = Math.round((100 * state.multiplier + (clutch ? CLUTCH_BONUS : 0)) * (state.turbo ? TURBO_POINTS : 1));
      if (state.rush > 0) {
        points *= 2;
        state.rush -= 1;
      }
      if (state.streak % RUSH_EVERY === 0) {
        state.rush = RUSH_GATES;
        rushStarted = true;
      }
      state.score += points;
      if (timed && !state.practice && state.streak % STREAK_TIME_EVERY === 0) timeAdded = addTime(STREAK_TIME_SEC);
    } else {
      state.streak = 0;
      state.multiplier = 1;
      state.rush = 0;
      state.misses.push({
        letter: row.stage.letter,
        category: row.stage.category,
        chosen: chosen?.word,
        answer: answer.word,
      });
      if (!state.practice && !spared) {
        if (timed) {
          state.timeLeft = Math.max(0, state.timeLeft - WRONG_PENALTY_SEC);
        } else {
          state.lives -= 1;
        }
        rescue = endOrRescue();
      }
    }

    return {
      correct,
      chosen,
      answer,
      points,
      clutch: correct && clutch,
      rushStarted,
      timeAdded,
      rescue,
      stage: row.stage,
      tier: row.tier,
      stageClear: row.lastInStage ? { correct: stageCorrect, total: row.stage.length } : null,
      snapshot: { ...state },
    };
  }

  // Called whenever the run may have just run out of time or erasers.
  function endOrRescue() {
    if (timed ? state.timeLeft > 0 : state.lives > 0) return null;
    const lucky = !state.rescued && state.elapsed >= rescueMinSec && rescueRng.next() < rescueChance;
    if (!lucky) {
      state.over = true;
      return null;
    }
    state.rescued = timed ? 'mummy' : 'nepa';
    state.grace = 1;
    if (timed) state.timeLeft = RESCUE_TIME_SEC;
    else state.lives = 1;
    return state.rescued;
  }

  function addTime(sec) {
    const added = Math.min(sec, TIME_BONUS_CAP - state.timeAdded);
    state.timeLeft += added;
    state.timeAdded += added;
    return added;
  }

  function collectClock() {
    if (state.over || !timed) return null;
    state.clocks += 1;
    return { added: addTime(CLOCK_SEC), snapshot: { ...state } };
  }

  const nextLetter = () => letters[state.letters.length] ?? null;

  function collectLetter() {
    const letter = nextLetter();
    if (state.over || letter === null) return null;
    state.letters = [...state.letters, letter];
    return { letter, index: state.letters.length - 1, done: state.letters.length === letters.length, snapshot: { ...state } };
  }

  function toggleTurbo() {
    state.turbo = !state.turbo;
    return state.turbo;
  }

  function collectStar() {
    if (state.over) return null;
    state.stars += 1;
    state.score += STAR_POINTS;
    let passReady = false;
    // Stars caught while a pass is ready or running score but don't fill the meter, so passes can't chain.
    if (state.pass <= 0 && !state.passReady) {
      state.meter += 1;
      if (state.meter >= STAR_METER) {
        state.passReady = true;
        passReady = true;
      }
    }
    return { points: STAR_POINTS, passReady, snapshot: { ...state } };
  }

  function activatePass() {
    if (state.over || !state.passReady || state.pass > 0) return null;
    state.passReady = false;
    state.meter = 0;
    state.pass = HALL_PASS_SEC;
    state.passes += 1;
    let spareLife = false;
    if (mode === 'endless' && state.lives < maxLives && bonusRng.next() < SPARE_ON_PASS) {
      state.lives += 1;
      state.spares += 1;
      spareLife = true;
    }
    return { spareLife, snapshot: { ...state } };
  }

  function collectSpare() {
    if (state.over) return null;
    state.spares += 1;
    if (state.lives < maxLives) {
      state.lives += 1;
      return { life: true, points: 0, snapshot: { ...state } };
    }
    state.score += SPARE_BONUS;
    return { life: false, points: SPARE_BONUS, snapshot: { ...state } };
  }

  function hitObstacle() {
    if (state.over) return null;
    if (state.pass > 0) {
      state.obstaclesSmashed += 1;
      return { shielded: true, lost: 0, droppedMultiplier: false, snapshot: { ...state } };
    }
    state.obstaclesHit += 1;
    const starsLost = state.passReady ? 0 : Math.ceil(state.meter / 2);
    state.meter -= starsLost;
    const lost = Math.min(state.score, OBSTACLE_PENALTY);
    state.score -= lost;
    const hadMultiplier = state.multiplier;
    state.streak = dropTier(state.streak);
    state.multiplier = multiplierFor(state.streak);
    state.rush = 0;
    return { lost, starsLost, droppedMultiplier: hadMultiplier > state.multiplier, snapshot: { ...state } };
  }

  function clearObstacle() {
    state.obstaclesCleared += 1;
  }

  function tick(dt) {
    if (state.over) return;
    state.elapsed += dt;
    if (state.pass > 0) state.pass = Math.max(0, state.pass - dt);
    if (!timed || state.practice) return;
    state.timeLeft = Math.max(0, state.timeLeft - dt);
    endOrRescue();
  }

  return {
    state,
    seed,
    nextItem,
    collectSpare,
    collectClock,
    collectLetter,
    nextLetter,
    enterStage,
    resolveGate,
    collectStar,
    activatePass,
    addDistance: (d) => {
      state.distance += d;
    },
    hitObstacle,
    clearObstacle,
    tick,
    toggleTurbo,
    speedFactor: () => {
      const running = speedFor(state.total, state.streak, state.stagesCleared) * (state.turbo ? TURBO_SPEED : 1);
      // The dash always feels like a boost, even on top of turbo.
      return state.pass > 0 ? Math.max(running * 1.25, speedFor(state.total, 0, state.stagesCleared) * DASH_SPEED) : running;
    },
    rampFactor: () => speedFor(state.total, 0, state.stagesCleared),
  };
}

function buildDecoys({ tier, letter, category, answer, rng, taken }) {
  const otherCats = RUN_CATEGORIES.filter((c) => c !== category);
  const sameLetterOtherCats = otherCats.flatMap((c) => poolFor(letter, c));
  const prefix = norm(answer).slice(0, 2);
  const nearPool = sameLetterOtherCats.filter((w) => norm(w).startsWith(prefix));
  const otherLetterWords = ALL_WORDS.filter((x) => x.letter !== letter);
  const bothWrong = otherLetterWords.filter((x) => x.category !== category);
  const sameCatOtherLetter = otherLetterWords.filter((x) => x.category === category);

  const wrongBoth = () => rng.pick(bothWrong).word;
  const wrongLetterSameCat = () => rng.pick(sameCatOtherLetter).word;
  const sameLetterWrongCat = () => rng.pick(sameLetterOtherCats);
  const nearMiss = () => (nearPool.length && rng.next() < 0.85 ? rng.pick(nearPool) : sameLetterWrongCat());

  const recipes = {
    easy: [wrongBoth, wrongBoth],
    medium: [wrongLetterSameCat, sameLetterWrongCat],
    hard: [sameLetterWrongCat, sameLetterWrongCat],
    near: [nearMiss, nearMiss],
  }[tier];

  const acceptable = (w) => w && !taken.has(norm(w)) && !isInCategory(w, letter, category);

  return recipes.map((make) => {
    let word = null;
    for (let tries = 0; tries < 24 && !word; tries++) {
      const candidate = make();
      if (acceptable(candidate)) word = candidate;
    }
    if (!word) {
      const sameLetter = tier === 'easy' ? [] : sameLetterOtherCats.filter(acceptable);
      word = sameLetter.length ? rng.pick(sameLetter) : null;
    }
    while (!word) {
      const candidate = wrongBoth();
      if (acceptable(candidate)) word = candidate;
    }
    taken.add(norm(word));
    return word;
  });
}

// The run is marked out of 100 like a script. Gates passed during a Hall Pass score
// whatever the player picks, so they don't count towards accuracy, and accuracy below 60% earns no marks at all.
export const MARKS = { accuracy: 55, clean: 15, work: 15, streak: 10, stars: 5 };
export const FULL_WORK_GATES = 25;
export function marksFor({ correct = 0, total = 0, obstaclesHit = 0, skipped = 0, bestStreak = 0, stars = 0, dashGates = 0 }) {
  const own = total - dashGates;
  const acc = own > 0 ? (correct - dashGates) / own : 0;
  const parts = {
    accuracy: MARKS.accuracy * Math.max(0, Math.min(1, (acc - 0.6) / 0.4)),
    clean: Math.max(0, MARKS.clean - obstaclesHit * 3 - skipped),
    work: MARKS.work * Math.min(1, own / FULL_WORK_GATES),
    streak: MARKS.streak * Math.min(1, bestStreak / 15),
    stars: MARKS.stars * Math.min(1, stars / STAR_METER),
  };
  const rounded = Object.fromEntries(Object.entries(parts).map(([k, v]) => [k, Math.round(v)]));
  return { ...rounded, total: Object.values(rounded).reduce((a, b) => a + b, 0), accuracy01: acc };
}

export function gradeFor(state) {
  if (!state.total) return 'F';
  const { total } = marksFor(state);
  if (total >= 95) return 'A+';
  if (total >= 88) return 'A';
  if (total >= 75) return 'B';
  if (total >= 60) return 'C';
  if (total >= 45) return 'D';
  return 'F';
}

export const RUN_URL = 'https://name-it-ashy.vercel.app/run';

export function shareText(state, { newBest } = {}) {
  const score = state.score.toLocaleString('en-US');
  const grade = gradeFor(state);
  const klass = state.mode === 'endless' && state.classIndex > 0 ? [`Made it to ${CLASSES[state.classIndex]} 🎓`] : [];
  const saved = state.rescued === 'nepa' ? ['Up NEPA saved my run 💡'] : state.rescued === 'mummy' ? ['Mummy came just in time 🙌'] : [];
  const lines = [`I scored ${score} on NameIt Run (${grade}) ✏️🏃`, ...klass, ...saved, ...(newBest ? ['New personal best! 🎉'] : []), 'Think you can beat me? 👇'];
  return [...lines, RUN_URL].join('\n');
}

export { TIERS };
